import { fetchCommentsForPatient } from '@24sens/ecg01-comments';
import { TargetTypeEnum } from '@24sens/ecg01-rest-client';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { PatientDetailsCard } from '../Cards/PatientDetailsCard';
import { TimelineCard } from '../Cards/TimelineCardOverview';
import { useLayout } from '../layout/useLayout';
import { fetchRecordingsPage } from '../recording';
import * as selectors from '../store/selectors';
import { useAppDispatch, useAppSelector } from '../store/store';
import { EnterCommentModal } from './EnterCommentModal';

export const PatientTimeline: React.FC = () => {
  const { t } = useTranslation();
  const isPrint = useMediaQuery('print');
  const { patientId } = useParams<{ patientId: string }>();
  const patient = useAppSelector((state) => selectors.patient.selectById(state, patientId));
  const [commentId, setCommentId] = useState<string | null>(null);
  const patientIdNum = Number.parseInt(patientId);
  const timelineEntries = useAppSelector((state) => selectors.timeline.pageEntries(state, patientIdNum));
  const paginationState = useAppSelector((state) => state.recordingPagination.pagesByPatient[patientIdNum]);
  const isLoading = useAppSelector((state) => state.recordingPagination.loading === 'pending');
  const dispatch = useAppDispatch();
  const { setTitle } = useLayout();

  const fabStyle: SxProps = {
    position: 'fixed',
    bottom: 16,
    right: 16,
    visibility: isPrint ? 'hidden' : 'visible',
  };

  useEffect(() => {
    if (patientId) {
      dispatch(fetchRecordingsPage({ userId: patientIdNum }));
      dispatch(fetchCommentsForPatient(patientIdNum));
    }
  }, [patientId]);

  useEffect(() => {
    setTitle(patient?.name + ' ' + t('dashboard.patientHeading'));
  }, [patient]);

  return (
    <>
      <Stack spacing={1} sx={{ display: isPrint ? 'block' : 'flex' }}>
        <PatientDetailsCard />
        {timelineEntries.map((entry) => (
          <TimelineCard key={entry.id} entry={entry} setOpenEnterComment={setCommentId} />
        ))}
        {(paginationState?.previousCursor != null || paginationState?.nextCursor != null) && (
          <Box className="No-Print" sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, pb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              disabled={paginationState?.previousCursor == null || isLoading}
              onClick={() => dispatch(fetchRecordingsPage({ userId: patientIdNum, cursor: paginationState.previousCursor }))}
            >
              {t('dashboard.recordingsNewer')}
            </Button>
            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              disabled={paginationState?.nextCursor == null || isLoading}
              onClick={() => dispatch(fetchRecordingsPage({ userId: patientIdNum, cursor: paginationState.nextCursor }))}
            >
              {t('dashboard.recordingsOlder')}
            </Button>
          </Box>
        )}
      </Stack>
      <Fab
        className={'No-Print'}
        color="secondary"
        aria-label="add"
        sx={{ ...fabStyle, bottom: '76px' }}
        onClick={doPrint}
      >
        <DownloadRoundedIcon />
      </Fab>
      <Fab className={'No-Print'} color="secondary" aria-label="add" sx={fabStyle} onClick={() => setCommentId('')}>
        <AddCommentIcon />
      </Fab>
      <EnterCommentModal
        open={commentId != null}
        handleClose={() => setCommentId(null)}
        patientOrRecordingId={Number.parseInt(patientId)}
        commentId={commentId ? Number.parseInt(commentId): undefined}
        target_type={TargetTypeEnum.User}
      />
    </>
  );
};

export function doPrint() {
  window.print();
}
